/*
 * -----------------------------------------------------------------------
 *  <copyright file="select-with-icons.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import cn from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'hooks';

import './styles.scss';
import { setAttribute, textToId } from '@utils/automation-test-helpers';

interface IselectWithIcons {
    items: any[];
    fieldName: string;
    onChange: (e: DropDownListChangeEvent) => void;
    selectedValue: any;
    width?: number;
    disabled?: boolean;
    className?: string;
    popupClassName?: string;
    'data-testid'?: string;
}

const SelectIconItem = ({ item, fieldName }: any) => {
    const label = item ? item[fieldName] : '';
    return (
        <div className="select-icon-item" data-testid={`list-item-${textToId(label)}`}>
            <FontAwesomeIcon icon={['fal', item?.icon || 'times']} className="fa-lg select-icon" />
            <p className="select-icon-text">{label}</p>
        </div>
    );
};

const SelectWithIcons = ({
    items,
    fieldName,
    onChange,
    selectedValue,
    width,
    disabled,
    className,
    popupClassName,
    'data-testid': testId,
}: IselectWithIcons) => {
    const { t } = useTranslation();

    const itemRender = (li: any, itemProps: any) => {
        const itemChildren = <SelectIconItem item={itemProps.dataItem} fieldName={fieldName} />;
        return React.cloneElement(li, li.props, itemChildren);
    };

    const ref = useRef(null);

    useEffect(() => {
        if (testId) {
            setAttribute(ref, 'data-testid', testId);
        }
    });

    return (
        <DropDownList
            ref={ref}
            data={items}
            defaultValue={items[0]}
            value={selectedValue}
            disabled={disabled}
            popupSettings={{
                className: cn('select-with-icons-popup', popupClassName),
            }}
            valueRender={(element, value) => (
                <div className="selected-select-icon-item">
                    {value?.icon && <FontAwesomeIcon icon={['fal', value?.icon || '']} className="fa-lg select-icon" />}
                    <p className="select-icon-text">{value ? value[fieldName] : ''}</p>
                </div>
            )}
            itemRender={itemRender}
            className={cn('select-icon-dropdown', className)}
            onChange={onChange}
            style={{ width: width ?? 140 }}
            listNoDataRender={() => <div className="dropdown-no-results">{t('noResults')}</div>}
        />
    );
};

export default SelectWithIcons;
